import type { KeyboardEvent, ReactNode } from 'react'

export interface TabItem<T extends string> {
  value: T
  label: ReactNode
  /** A small count after the label. */
  count?: number
}

/** A segmented control: a sunken track with the chosen part raised. Arrow keys move between parts. */
export function Tabs<T extends string>({
  value,
  items,
  onChange,
  label,
  inline = false,
}: {
  value: T
  items: readonly TabItem<T>[]
  onChange: (value: T) => void
  /** Read by screen readers: what the tabs choose between. */
  label: string
  /** Size to its content instead of filling the row. */
  inline?: boolean
}) {
  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    const i = items.findIndex((t) => t.value === value)
    const next = items[(i + (e.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length]
    if (!next) return
    e.preventDefault()
    onChange(next.value)
    const buttons = e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')
    buttons[items.indexOf(next)]?.focus()
  }
  return (
    <div className={`tabs${inline ? ' tabs--inline' : ''}`} role="tablist" aria-label={label} onKeyDown={move}>
      {items.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          className="tabs__tab"
          aria-selected={t.value === value}
          tabIndex={t.value === value ? 0 : -1}
          onClick={() => onChange(t.value)}
        >
          {t.label}
          {t.count !== undefined && <span className="tabs__count">{t.count}</span>}
        </button>
      ))}
    </div>
  )
}
